import React from "react";
import { Box, Chip, Divider, Paper, Typography } from "@mui/material";
import TwoColumn from "./TwoColumn";
import { getWeight, getWeightLabel, STATUS_CONFIG } from "../../constants";
import { ProgressIndicator } from "../ProjectList/ProjectProgress";

/**
 * Sidebar panel showing project status, type, priority, progress, and details.
 */
const ProjectSidebar = ({
  projectInfo,
  actions,
  participants,
  estimatedToResolve,
  objMeta,
  onStatusClick,
}) => {
  const formatDate = (ts) => {
    if (!ts) return "—";
    const d = ts.toDate ? ts.toDate() : new Date(ts);
    return d.toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" });
  };

  const w = getWeight(projectInfo);
  const label = getWeightLabel(w);
  const priorityColor = w >= 8 ? "error" : w >= 5 ? "warning" : w >= 3 ? "info" : "default";

  const tasksDone = (actions || []).filter((a) => a.status === "done").length;
  const tasksTotal = (actions || []).length;
  const taskProgress = tasksTotal > 0 ? Math.round((tasksDone / tasksTotal) * 100) : 0;

  return (
    <Paper
      elevation={0}
      sx={{ p: 2.5, borderRadius: 3, border: "1px solid #e0e0e0" }}
    >
      <Typography variant="subtitle2" fontWeight={600} color="text.secondary" gutterBottom>
        Status
      </Typography>
      <Box sx={{ mb: 2 }}>
        <Chip
          label={STATUS_CONFIG[projectInfo.status]?.label || projectInfo.status || "Open"}
          color={STATUS_CONFIG[projectInfo.status]?.color || "info"}
          onClick={onStatusClick}
          sx={{ cursor: "pointer", fontWeight: 600 }}
        />
      </Box>

      {/* Project Type */}
      <Box sx={{ mb: 2 }}>
        <Typography variant="subtitle2" fontWeight={600} color="text.secondary" gutterBottom>
          Type
        </Typography>
        {objMeta ? (
          <Chip
            icon={React.createElement(objMeta.icon, { sx: { fontSize: 16 } })}
            label={objMeta.label}
            size="small"
            color={objMeta.chipColor}
          />
        ) : (
          <Chip label="Not classified" size="small" variant="outlined" />
        )}
      </Box>

      {/* Priority / Weight */}
      <Box sx={{ mb: 2 }}>
        <Typography variant="subtitle2" fontWeight={600} color="text.secondary" gutterBottom>
          Priority
        </Typography>
        <Chip label={`${label} (${w})`} size="small" color={priorityColor} />
      </Box>

      {/* Task Progress */}
      {tasksTotal > 0 && (
        <Box sx={{ mb: 2 }}>
          <Typography variant="subtitle2" fontWeight={600} color="text.secondary" gutterBottom>
            Task Progress
          </Typography>
          <ProgressIndicator value={taskProgress} />
        </Box>
      )}

      <Divider sx={{ my: 1.5 }} />

      <Typography variant="subtitle2" fontWeight={600} color="text.secondary" gutterBottom>
        Details
      </Typography>
      <TwoColumn
        leftColumnView="Reporter"
        RightColumnView={participants?.[projectInfo.creator]?.displayName || "Unknown"}
      />
      <TwoColumn
        leftColumnView="Reported On"
        RightColumnView={formatDate(projectInfo.timestamp || projectInfo.createdAt)}
      />
      <TwoColumn
        leftColumnView="Estimate"
        RightColumnView={estimatedToResolve || "—"}
      />
      {projectInfo.estimatedBudget && (
        <TwoColumn
          leftColumnView="Budget"
          RightColumnView={`₹${projectInfo.estimatedBudget}`}
        />
      )}
    </Paper>
  );
};

export default ProjectSidebar;
